/**
 * LOCK window — the lockpicking minigame, in its own draggable GameWindow.
 * Renders a `PickSession` (sim/lockpick.ts): the pin columns (all drawn at a
 * UNIFORM height even when they hold different numbers of pins — the extra top
 * cells are inert), the pick's cursor + lift, seated/jammed pins, the active
 * tool, a brief input tutorial, and the CLICK!/CLACK! feedback.
 *
 * NON-suspending: the world keeps running behind it (guards can still catch you
 * mid-pick). Keyboard-only for play — main.ts routes ←/→/↑/↓, Tab, Space, E/Esc,
 * and WASD. A dev row (Randomize + a select-only budget) appears only while the
 * ` debug overlay is on; those controls are mouse-operated.
 *
 * No PixiJS.
 */

import { LOCK_BUDGET_PRESETS } from '../sim/lock';
import type { PickSession } from '../sim/lockpick';
import type { GameWindow } from './window';

export class LockWindow {
  private readonly window: GameWindow;
  private open = false;
  private readonly rootEl: HTMLElement;
  private readonly toolEl: HTMLElement;
  private readonly colsEl: HTMLElement;
  private readonly feedbackEl: HTMLElement;
  private readonly tutEl: HTMLElement;
  private readonly devEl: HTMLElement;
  private readonly budgetSel: HTMLSelectElement;
  /** The single floating '~' rake tool — one glyph that slides across the columns,
   *  positioned/animated in updateRakeCursor. Persistent (the colsEl innerHTML is
   *  rebuilt each update, so it lives on the root instead). */
  private readonly rakeCursor: HTMLElement;

  constructor(win: GameWindow, onRandomize: (budget: number) => void) {
    this.window = win;
    const root = document.createElement('div');
    root.className = 'lw-root';
    root.innerHTML = `
      <div class="lw-tool"></div>
      <div class="lw-cols"></div>
      <div class="lw-feedback"></div>
      <div class="lw-tut"></div>
      <div class="lw-dev">
        <label class="lw-dev-label">budget <select class="lw-budget" tabindex="-1"></select></label>
        <button class="lw-randomize" type="button" tabindex="-1">⟳ Randomize</button>
      </div>`;
    this.toolEl = root.querySelector('.lw-tool') as HTMLElement;
    this.colsEl = root.querySelector('.lw-cols') as HTMLElement;
    this.feedbackEl = root.querySelector('.lw-feedback') as HTMLElement;
    this.tutEl = root.querySelector('.lw-tut') as HTMLElement;
    this.devEl = root.querySelector('.lw-dev') as HTMLElement;
    this.budgetSel = root.querySelector('.lw-budget') as HTMLSelectElement;

    for (const b of LOCK_BUDGET_PRESETS) {
      const opt = document.createElement('option');
      opt.value = String(b);
      opt.textContent = `${b} pins`;
      this.budgetSel.appendChild(opt);
    }
    this.budgetSel.value = String(LOCK_BUDGET_PRESETS[Math.floor(LOCK_BUDGET_PRESETS.length / 2)]);
    // Blur after picking a value so the arrow keys go back to the lock, not the select.
    this.budgetSel.addEventListener('change', () => this.budgetSel.blur());
    const btn = root.querySelector('.lw-randomize') as HTMLButtonElement;
    btn.addEventListener('click', () => {
      onRandomize(Number(this.budgetSel.value));
      btn.blur();
    });

    this.rootEl = root;
    const rc = document.createElement('div');
    rc.className = 'lw-rake-cursor';
    rc.textContent = '~';
    rc.style.display = 'none';
    root.appendChild(rc); // absolutely positioned relative to .lw-root
    this.rakeCursor = rc;

    this.window.body.appendChild(root);
    this.window.root.style.display = 'none';
  }

  isOpen(): boolean {
    return this.open;
  }

  show(s: PickSession, devVisible: boolean): void {
    this.open = true;
    this.window.root.style.display = '';
    this.window.raise();
    this.update(s, devVisible);
  }

  hide(): void {
    this.open = false;
    this.window.root.style.display = 'none';
  }

  /** Reflect the session state (call after each key action + on show). */
  update(s: PickSession, devVisible: boolean): void {
    // Always show which tool is selected (the other is dimmed) — no "can't rake".
    this.toolEl.innerHTML = `SECURITY ${s.securityLevel} · TOOL: ` +
      `<span class="${s.tool === 'rake' ? 'lw-tool-sel' : ''}">RAKE</span> / ` +
      `<span class="${s.tool === 'pick' ? 'lw-tool-sel' : ''}">PICK</span>`;

    // Every column offers the SAME number of interactable slots (gridHeight) — as
    // many as the deepest column's pins. Which slot seats is hidden; you feel it
    // out. A picked column is only highlighted green, not filled in.
    const fb = s.feedback;
    // The RAKE is its own tool — no per-column cursor (raking scrubs every column
    // at once). Instead ONE floating '~' rests under the columns and slides across
    // them on each pass (see updateRakeCursor). The sweep is IDENTICAL whether it
    // seats, bites nothing, or hits a lock it can't work, so the player gets no tell.
    // (feedback is cleared on tool-toggle, so a stale pick 'click' can't fire it.)
    const rakeMode = s.tool === 'rake';
    const rakeSweep = rakeMode && (fb === 'rake' || fb === 'click');
    const gridH = s.gridHeight;
    let html = '';
    s.columns.forEach((c, i) => {
      // While raking there is no active column and no pick cursor at all.
      const active = !rakeMode && i === s.cursor;
      const jammed = c.falseSet && !c.set;
      // The pick rests OUTSIDE (below) the columns at lift -1 and only enters a
      // column when the player ↑s into it — so it's drawn in an under-slot until
      // then, not overlapping the bottom pin.
      const belowPick = active && !c.set && s.lift < 0;
      let cells = '';
      for (let r = gridH - 1; r >= 0; r--) {
        let cls = 'lw-cell';
        let ch = '·'; // an interactable slot; the pin height inside is hidden
        if (active && !c.set && r === s.lift) { cls += ' lw-pick'; ch = '◄'; }
        cells += `<div class="${cls}">${ch}</div>`;
      }
      let colCls = 'lw-col';
      if (c.set) colCls += ' lw-seated';
      else if (active) colCls += ' lw-active';
      if (jammed) colCls += ' lw-false';
      const badge = c.set ? '✔' : jammed ? '✗' : '&nbsp;';
      // Under-slot: the resting '◄' below the active column in PICK mode; else empty
      // but reserved, so every column's number label stays aligned (and the floating
      // rake '~' has a clear band to travel along).
      const underCls = 'lw-under' + (belowPick ? ' lw-pick' : '');
      const under = `<div class="${underCls}">${belowPick ? '◄' : '&nbsp;'}</div>`;
      html += `<div class="${colCls}"><div class="lw-badge">${badge}</div><div class="lw-stack">${cells}</div>${under}<div class="lw-colnum">${i + 1}</div></div>`;
    });
    this.colsEl.innerHTML = html;
    this.updateRakeCursor(rakeMode, rakeSweep);

    // CLACK is said ONLY for a genuine false set. A re-poked jam or a failed rake
    // shows no text (the red column / red flash carries the message).
    this.feedbackEl.textContent =
      fb === 'click' ? 'CLICK!'
      : fb === 'clack' ? 'CLACK!'
      : fb === 'open' ? '★ LOCK OPEN'
      : fb === 'reset' ? '… tension released'
      : '';
    let fbCls = 'lw-feedback';
    if (fb === 'click' || fb === 'open') fbCls += ' lw-good';
    else if (fb === 'clack') fbCls += ' lw-bad';
    this.feedbackEl.className = fbCls;

    // Tool-specific controls (Space does different things per tool).
    this.tutEl.innerHTML = s.tool === 'rake'
      ? `<div><b>Space</b> rake the lock&nbsp; <b>Tab</b> switch to pick</div>` +
        `<div><b>WASD</b> drop &amp; move away</div>`
      : `<div><b>←/→</b> move&nbsp; <b>↑</b> enter/lift&nbsp; <b>↓</b> try to set&nbsp; <b>Space</b> reset</div>` +
        `<div><b>Tab</b> switch to rake&nbsp; <b>WASD</b> drop &amp; move away</div>`;

    this.devEl.style.display = devVisible ? '' : 'none';
  }

  /** Place the single floating '~' rake cursor on the band just below the columns.
   *  It rests over the first column; on a pass (`sweep`) it slides right across every
   *  column and back — one glyph dragged over the pins, the same motion every time
   *  (no tell). Hidden entirely in pick mode. */
  private updateRakeCursor(rakeMode: boolean, sweep: boolean): void {
    const rc = this.rakeCursor;
    const cols = this.colsEl.querySelectorAll<HTMLElement>('.lw-col');
    if (!rakeMode || cols.length === 0) {
      rc.style.display = 'none';
      rc.classList.remove('lw-rake-sweep');
      return;
    }
    const host = this.rootEl.getBoundingClientRect();
    const first = cols[0]!.getBoundingClientRect();
    const last = cols[cols.length - 1]!.getBoundingClientRect();
    const stack = cols[0]!.querySelector('.lw-stack')!.getBoundingClientRect();
    const xStart = first.left + first.width / 2 - host.left;
    const xEnd = last.left + last.width / 2 - host.left;
    rc.style.display = '';
    rc.style.left = `${xStart}px`;
    rc.style.top = `${stack.bottom - host.top + 2}px`; // the under band, just below the stacks
    rc.style.setProperty('--rakeDX', `${xEnd - xStart}px`); // how far right the last column is
    // Re-fire the slide from scratch each pass (remove → force reflow → add).
    rc.classList.remove('lw-rake-sweep');
    if (sweep) {
      void rc.offsetWidth;
      rc.classList.add('lw-rake-sweep');
    }
  }
}
